import { BATTLE_STATES, COMMANDERS, LevelSlice, ManaByPlayer, Player, Spell, TURN_STATES } from './store.d'
import { StateCreator } from 'zustand'
import produce from 'immer'
import { LevelInitialState } from './initialState'
import { GameSceneStoreStype } from '../../a.scenes/GameScene/store'
import { Vector2, Vector3 } from 'three'
import { CHARACTER_TYPES, MAGIC_TYPE } from '../../f.assets/Characters/scripts/constants'

export const createLevelSlice: StateCreator<GameSceneStoreStype, [['zustand/devtools', never]], [], LevelSlice> = (
  set,
  get,
) => ({
  // API: simplyfied actions that hide the state management
  levels: {
    data: {
      ...LevelInitialState.data,
    },
    resetLevel: () =>
      set(
        produce(state => {
          state.levels.data = LevelInitialState.data
        }),
        false,
        { type: 'resetLevel' },
      ),
    checkItemToGo: (task: string) =>
      set(
        produce(state => {
          state.levels.data.checklistToGo[task] = true
        }),
        false,
        { type: 'checkItemToGo' },
      ),
    setBattleState: (battleState: BATTLE_STATES) =>
      set(
        produce(state => {
          state.levels.data.battleState = battleState
        }),
        false,
        { type: 'setBattleState' },
      ),
    setTurnState: (turnState: TURN_STATES) =>
      set(
        produce((state: GameSceneStoreStype) => {
          state.levels.data.turnState = turnState
        }),
      ),
    setTurnOwner: (owner: COMMANDERS) =>
      set(
        produce(state => {
          state.levels.data.turnOwner = owner
        }),
        false,
        { type: 'setTurnOwner' },
      ),
    bootUp: () => {
      const store = get()
      store.levels.setTurnOwner(store.levels.data.troopOwners[0])
    },
    startBattle: () => {
      // set up turn owner
      // API.setCurrentPlayer(useLevelState.getState().participants[0])

      const store = get()
      // set battle state
      store.levels.setBattleState(BATTLE_STATES.ONGOING)
      // unlock interactions (if turn owner is player)
      store.terrain.unlockInteraction()
    },
    hasAvailableUnits: () => {
      return true
    },
    isActionableUnit: (coords: Vector2) => {
      const charState = get().characters.getCharacterStateByCoords(coords)
      // unit belongs to turn owner
      // unit can act

      return charState !== undefined
    },
    isTargetUnit: (coords: Vector2) => {
      const charState = get().characters.getCharacterStateByCoords(coords)
      // is enemy?
      // is alive?
      return charState !== undefined
    },
    setPlayers: (players: Player[]) =>
      set(
        produce(state => {
          state.levels.data.players = players.map((p: Player) => {
            const shuffledSpells = [...p.spells].sort(() => 0.5 - Math.random())
            const activeSpells = shuffledSpells.splice(0, 3)
            const spells = shuffledSpells
            return {
              ...p,
              spells,
              activeSpells,
            }
          })
        }),
        false,
        { type: 'setPlayers' },
      ),
    setManaConfig: (commander: COMMANDERS, config: ManaByPlayer) => set(
      produce(state => {
        state.levels.data.manaLevelsByPlayer = {...state.levels.data.manaLevelsByPlayer, [commander]: config}
      }),
      false,
        { type: 'setManaConfig' },
    ),
    updateMana: (commander: COMMANDERS, diff: number) =>
      set(
        produce(state => {
          const manaData = state.levels.data.manaLevelsByPlayer[commander]
          manaData.manaLevel = Math.min(manaData.manaLevel + diff, manaData.maxManaLevel)
        }),
        false,
        { type: 'updateMana' },
      ),
    // idx is the position of the selected spell into the player.spells array
    setSelectedSpellIdx: (idx: number) =>
      set(
        produce(state => {
          state.levels.data.selectedSpellIdx = idx
        }),
        false,
        { type: 'setSelectedSpellIdx' },
      ),
    // idx is the position of the selected spell into the player.spells array
    clearSelectedSpellIdx: () =>
      set(
        produce(state => {
          state.levels.data.selectedSpellIdx = undefined
        }),
        false,
        { type: 'clearSelectedSpellIdx' },
      ),
    setCastedSpell: (spell: Spell, owner: COMMANDERS) =>
      set(
        produce(state => {
          const player = state.levels.data.players.find((p: Player) => p.id === owner) as Player
          const spellIdx = player.activeSpells.findIndex(s => s.id === spell.id)

          const burnSpell = player.activeSpells.splice(spellIdx, 1)
          player.activeSpells.unshift(player.spells.shift()!)
          player.spells.push(burnSpell[0])

          state.levels.data.selectedSpellIdx = undefined
        }),
        false,
        { type: 'clearSelectedSpellIdx' },
      ),
    throwMagic: (commander: COMMANDERS, targetPosition: Vector3, magicType: MAGIC_TYPE) =>
      set(
        produce(state => {
          const player = state.levels.data.players.find((p: Player) => p.id === commander) as Player
          player.magicThrown = { destination: targetPosition, type: magicType, owner: commander }
        }),
        false,
        { type: 'throwMagic' },
      ),
    clearThrowMagic: (commander: COMMANDERS) =>
      set(
        produce(state => {
          const player = state.levels.data.players.find((p: Player) => p.id === commander) as Player
          player.magicThrown = undefined
        }),
        false,
        { type: 'throwMagic' },
      ),
    // setPlayerTeam: (team: CHARACTER_TYPES.JACK | CHARACTER_TYPES.WITCH) =>
    //   set(
    //     produce(state => {
    //       const players = state.levels.data.players
    //       const comTeam = team === CHARACTER_TYPES.JACK ? CHARACTER_TYPES.WITCH : CHARACTER_TYPES.JACK
    //       players.forEach((p: Player) => {
    //         p.team = p.id === COMMANDERS.PLAYER ? team : comTeam
    //       })
    //     }),
    //     false,
    //     { type: 'setPlayerTeam' },
    //   ),
  },
})
